import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ErrorMessage } from '@/components/ErrorMessage';
import { api, APIError } from '@/api/client';

export function CreateSOWPage() {
  const navigate = useNavigate();
  const [owner, setOwner] = useState('');
  const [items, setItems] = useState([{ title: '', description: '' }]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const addItem = () => {
    setItems([...items, { title: '', description: '' }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (
    index: number,
    field: 'title' | 'description',
    value: string
  ) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!owner.trim()) {
      setError('Owner name is required');
      return;
    }

    const validItems = items.filter(
      (item) => item.title.trim() && item.description.trim()
    );

    if (validItems.length === 0) {
      setError('At least one item with title and description is required');
      return;
    }

    setLoading(true);

    try {
      const response = await api.createSOW({
        owner: owner.trim(),
        items: validItems.map((item) => ({
          title: item.title.trim(),
          description: item.description.trim(),
        })),
      });

      navigate(`/sow/${response.data.id}`, {
        state: { token: response.data.clientAccessToken },
      });
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message);
      } else {
        setError('Failed to create SOW. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Create Statement of Work
        </h1>

        {error && <ErrorMessage message={error} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Owner Name *
            </label>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
              placeholder="Your name or team name"
              required
              maxLength={100}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Initial Items *
              </label>
              <button
                type="button"
                onClick={addItem}
                className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-300"
              >
                + Add Item
              </button>
            </div>

            {items.map((item, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">
                    Item {index + 1}
                  </span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => updateItem(index, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                  placeholder="Item title"
                  required
                />
                <textarea
                  value={item.description}
                  onChange={(e) =>
                    updateItem(index, 'description', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Item description"
                  rows={3}
                  required
                />
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-md font-semibold hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Creating...' : 'Create SOW'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
